import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';
import type { SkeletonPulseProps } from './Skeleton.types';
import { SkeletonPulseContext } from './SkeletonContext';

/**
 * Web: the group fades with the shared `animate-skeleton-pulse` keyframes
 * (emitted by the Tailwind preset from `skeleton-pulse.json`). Reduced motion
 * holds it at full opacity.
 *
 * With a label the region is a polite `role="status"` + `aria-busy`, and the
 * label is real (visually hidden) TEXT inside it — a status region announces
 * its content, not an `aria-label`. Without one the grey shapes are hidden from
 * assistive tech entirely.
 */
export function SkeletonPulse({
  children,
  className,
  accessibilityLabel,
  testID,
}: SkeletonPulseProps) {
  return (
    <SkeletonPulseContext.Provider value>
      <View
        className={cn(
          'pointer-events-none animate-skeleton-pulse motion-reduce:animate-none',
          className,
        )}
        testID={testID}
        webProps={
          accessibilityLabel
            ? { role: 'status', 'aria-busy': true, 'aria-live': 'polite' }
            : { 'aria-hidden': true }
        }>
        {accessibilityLabel ? (
          <span className="sr-only">{accessibilityLabel}</span>
        ) : null}
        {children}
      </View>
    </SkeletonPulseContext.Provider>
  );
}
